import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import { API_URL, resolveBackendPath, resolveUploadsPath, getLocalThumbnailUrl, getLocalImageUrl, getDriveThumbnailUrl, getDriveImageUrl } from '../config';
import { useBackgroundJobs } from '../context/BackgroundJobsContext';

const Batches = () => {
  const { addEncodingJob, addDeletionJob } = useBackgroundJobs();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [encodingInProgress, setEncodingInProgress] = useState(false);
  const [encodingProgress, setEncodingProgress] = useState(null);
  const [photoFilter, setPhotoFilter] = useState('all'); // 'all', 'encoded', 'not_encoded', 'pending', 'rejected'
  const [eventFilter, setEventFilter] = useState(''); // '' = all, '__none__' = no event, or event_id
  const navigate = useNavigate();

  const eventOptions = React.useMemo(() => {
    const opts = [{ value: '', label: 'All events' }];
    const seen = new Set();
    batches.forEach((b) => {
      const id = b.event_id != null ? b.event_id : '__none__';
      if (seen.has(id)) return;
      seen.add(id);
      opts.push({ value: id, label: b.event_name || 'No event' });
    });
    return opts;
  }, [batches]);

  const filteredBatches = React.useMemo(() => {
    if (!eventFilter) return batches;
    if (eventFilter === '__none__') return batches.filter((b) => b.event_id == null);
    return batches.filter((b) => b.event_id != null && Number(b.event_id) === Number(eventFilter));
  }, [batches, eventFilter]);
  const batchDetailsRequestIdRef = React.useRef(null);
  const encodingBatchIdRef = React.useRef(null);

  useEffect(() => {
    fetchBatches();
  }, []);

  // Poll for encoding status updates when encoding is in progress (use ref so switching batch doesn't change poll target)
  useEffect(() => {
    const batchId = encodingBatchIdRef.current;
    if (encodingInProgress && batchId) {
      let pollCount = 0;
      const maxPolls = 60; // Stop after 2 minutes (60 * 2 seconds)
      
      const interval = setInterval(async () => {
        pollCount++;
        
        // Fetch latest batch details
        try {
          const response = await axios.get(`${API_URL}/batches/${batchId}`);
          const updatedBatch = response.data;
          
          // Update progress based on actual encoding status
          if (updatedBatch?.photos) {
            const approvedPhotos = updatedBatch.photos.filter(p => p.status === 'approved');
            const encodedPhotos = approvedPhotos.filter(p => p.is_encoded);
            const total = approvedPhotos.length;
            const encoded = encodedPhotos.length;
            
            if (total > 0) {
              setEncodingProgress({
                current: encoded,
                total: total,
                message: `Encoding photos... ${encoded}/${total} completed`
              });
              
              if (batchDetailsRequestIdRef.current === null || batchDetailsRequestIdRef.current === batchId) {
                setSelectedBatch(updatedBatch);
              }
              if (encoded === total) {
                setEncodingInProgress(false);
                setEncodingProgress(null);
                encodingBatchIdRef.current = null;
                fetchBatches();
              }
            }
          }
          
          // Also refresh batch list
          fetchBatches();
        } catch (error) {
          console.error('Error polling batch status:', error);
        }
        
        // Stop polling after max attempts
        if (pollCount >= maxPolls) {
          setEncodingInProgress(false);
          setEncodingProgress(null);
          encodingBatchIdRef.current = null;
        }
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [encodingInProgress]);

  const fetchBatches = async () => {
    try {
      const response = await axios.get(`${API_URL}/batches`);
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (batchId) => {
    if (!window.confirm('Approve this batch? Photos will be encoded.')) return;
    encodingBatchIdRef.current = batchId;
    setEncodingInProgress(true);
    setEncodingProgress({ current: 0, total: 0, message: 'Starting encoding...' });
    
    try {
      const response = await axios.post(`${API_URL}/batches/${batchId}/approve`);
      const data = response.data;
      
      setEncodingProgress(null);
      setEncodingInProgress(false);
      const total = selectedBatch?.photos?.length ?? 0;
      if (total > 0) {
        addEncodingJob({
          batchId,
          total,
          label: `Batch #${selectedBatch?.batch_number ?? batchId}${selectedBatch?.user_name ? ' - ' + selectedBatch.user_name : ''}${selectedBatch?.event_name ? ' • ' + selectedBatch.event_name : ''}`,
          eventName: selectedBatch?.event_name || null,
        });
      }
      alert(data.message || 'Batch approved. Encoding queued.');
      fetchBatches();
      if (selectedBatch?.id === batchId) {
        fetchBatchDetails(batchId);
      }
    } catch (error) {
      setEncodingProgress(null);
      setEncodingInProgress(false);
      alert('Error: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleDelete = async (batchId) => {
    const batch = batches.find(b => b.id === batchId);
    const batchInfo = batch ? `Batch #${batch.batch_number} - ${batch.user_name}` : 'this batch';
    
    if (!window.confirm(`Are you sure you want to delete ${batchInfo}?\n\nThis will permanently delete:\n- The batch and all its photos\n- All photo files and thumbnails\n- All face encodings\n- All related database records\n\nThis action cannot be undone!`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API_URL}/batches/${batchId}`);
      
      const data = response.data;
      const message = data.queued != null
        ? (data.message || 'Batch deletion queued. Deletion continues in the background.')
        : `Batch deleted successfully!\n\nDeleted: ${data.deleted_files} photo files, ${data.deleted_thumbnails} thumbnails${(data.errors && data.errors.length > 0) ? `\n\nErrors:\n${data.errors.join('\n')}` : ''}`;
      alert(message);
      if (selectedBatch?.id === batchId) setSelectedBatch(null);
      addDeletionJob({
        batchId,
        total: batch?.photo_count ?? (selectedBatch?.id === batchId ? selectedBatch?.photos?.length : undefined),
        label: batch ? `Batch #${batch.batch_number} - ${batch.user_name}${batch.event_name ? ' • ' + batch.event_name : ''}` : `Batch #${batchId}`,
        eventName: batch?.event_name || null,
      });
      if (data.queued != null) {
        setBatches(prev => prev.filter(b => b.id !== batchId));
      } else {
        fetchBatches();
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const fetchBatchDetails = async (batchId) => {
    const requestId = batchId;
    batchDetailsRequestIdRef.current = requestId;
    setSelectedBatch((prev) => (prev?.id === batchId ? prev : { id: batchId, _loading: true }));
    setPhotoFilter('all');
    try {
      const response = await axios.get(`${API_URL}/batches/${batchId}`);
      if (batchDetailsRequestIdRef.current !== requestId) return;
      setSelectedBatch(response.data);
    } catch (error) {
      if (batchDetailsRequestIdRef.current !== requestId) return;
      console.error('Error fetching batch details:', error);
      setSelectedBatch((prev) => (prev?.id === batchId ? null : prev));
    } finally {
      if (batchDetailsRequestIdRef.current === requestId) {
        batchDetailsRequestIdRef.current = null;
      }
    }
  };

  const handlePhotoAction = async (photoId, action) => {
    try {
      await axios.post(`${API_URL}/photos/${photoId}/${action}`);
      alert(`Photo ${action}d!`);
      if (selectedBatch) {
        fetchBatchDetails(selectedBatch.id);
      }
      fetchBatches();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleRetryEncoding = async (photoId) => {
    if (!window.confirm('Retry encoding for this photo?')) return;
    try {
      const response = await axios.post(`${API_URL}/photos/${photoId}/retry-encoding`);
      alert(response.data.message || 'Encoding successful!');
      if (selectedBatch) {
        fetchBatchDetails(selectedBatch.id);
      }
      fetchBatches();
    } catch (error) {
      const errorMsg = error.response?.data?.details || error.response?.data?.error || 'Unknown error';
      alert('Encoding failed: ' + errorMsg);
    }
  };

  const handleRetryBatchEncoding = async (batchId) => {
    if (!window.confirm('Retry encoding for all unencoded photos in this batch?')) return;
    encodingBatchIdRef.current = batchId;
    setEncodingInProgress(true);
    setEncodingProgress({ current: 0, total: 0, message: 'Retrying encoding...' });
    
    try {
      const response = await axios.post(`${API_URL}/batches/retry-encoding/${batchId}`);
      const data = response.data;
      
      setEncodingProgress(null);
      setEncodingInProgress(false);
      const total = selectedBatch?.photos?.filter((p) => p.status === 'approved' && !p.is_encoded).length ?? 0;
      if (total > 0) {
        addEncodingJob({
          batchId,
          total,
          label: `Batch #${selectedBatch?.batch_number ?? batchId}${selectedBatch?.event_name ? ' • ' + selectedBatch.event_name : ''}`,
          eventName: selectedBatch?.event_name || null,
        });
      }
      alert(data.message || 'Encoding queued.');
      fetchBatches();
      if (selectedBatch?.id === batchId) {
        fetchBatchDetails(batchId);
      }
    } catch (error) {
      setEncodingProgress(null);
      setEncodingInProgress(false);
      alert('Error: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const statusBadgeCls = (s) => s === 'approved' ? 'bg-emerald-100 text-emerald-700' : s === 'rejected' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700';
  const photoStatusBadgeCls = (p) => p.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : p.status === 'rejected' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700';

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Batches</h1>
        <p className="text-sm text-slate-500 mt-0.5">{filteredBatches.length} of {batches.length} batches</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Batch list */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {eventOptions.length > 1 && (
              <div className="px-4 py-3 border-b border-slate-100">
                <select
                  value={eventFilter}
                  onChange={(e) => setEventFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:border-violet-500 bg-slate-50"
                >
                  {eventOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}
            {filteredBatches.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">No batches found</div>
            ) : (
              <ul className="divide-y divide-slate-50">
                {filteredBatches.map((batch) => (
                  <li
                    key={batch.id}
                    className={`px-4 py-3 cursor-pointer transition-colors hover:bg-slate-50 ${selectedBatch?.id === batch.id ? 'bg-violet-50 border-l-2 border-violet-500' : ''}`}
                    onClick={() => fetchBatchDetails(batch.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">
                          Batch #{batch.batch_number} · {batch.user_name}
                        </p>
                        {batch.event_name && <p className="text-xs text-violet-600 truncate mt-0.5">{batch.event_name}</p>}
                        <p className="text-[10px] text-slate-400 mt-1">
                          {batch.photo_count} photos · {batch.approved_count} approved · {batch.pending_count} pending
                        </p>
                        {batch.status === 'approved' && batch.approved_count > 0 && (
                          <p className="text-[10px] mt-1">
                            <span className={`px-1.5 py-0.5 rounded-full font-bold ${batch.unencoded_count > 0 ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                              {batch.encoded_count || 0}/{batch.approved_count} encoded{batch.unencoded_count > 0 ? ` (${batch.unencoded_count} pending)` : ''}
                            </span>
                          </p>
                        )}
                      </div>
                      <span className={`px-2 py-0.5 text-[10px] rounded-full flex-shrink-0 font-bold ${statusBadgeCls(batch.status)}`}>
                        {batch.status}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Batch detail */}
        <div className="lg:col-span-3">
          {selectedBatch ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              {selectedBatch._loading ? (
                <div className="flex items-center justify-center py-16 gap-3">
                  <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
                  <span className="text-slate-500 text-sm">Loading batch…</span>
                </div>
              ) : (
                <div className="p-5">
                  {/* Batch header */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base font-bold text-slate-900">Batch #{selectedBatch.batch_number}</h2>
                      {selectedBatch.status === 'approved' && selectedBatch.photos && (() => {
                        const approved = selectedBatch.photos.filter(p => p.status === 'approved');
                        const encoded = approved.filter(p => p.is_encoded).length;
                        const total = approved.length;
                        if (total === 0) return null;
                        const unencoded = total - encoded;
                        return (
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-slate-500">Encoding:</span>
                            <span className="text-xs font-medium text-emerald-600">{encoded} encoded</span>
                            {unencoded > 0 && <span className="text-xs font-medium text-amber-600">{unencoded} pending</span>}
                            <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-1.5 bg-violet-500 rounded-full" style={{ width: `${total > 0 ? (encoded / total) * 100 : 0}%` }} />
                            </div>
                            <span className="text-xs text-slate-400">{encoded}/{total}</span>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                      {selectedBatch.status === 'approved' && selectedBatch.photos?.some(p => p.status === 'approved' && !p.is_encoded) && (
                        <button onClick={() => handleRetryBatchEncoding(selectedBatch.id)}
                          className="px-3 py-1.5 rounded-xl text-xs font-bold text-white transition-all"
                          style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                          Retry Encoding
                        </button>
                      )}
                      {selectedBatch.status !== 'approved' && (
                        <button onClick={() => handleApprove(selectedBatch.id)}
                          className="px-3 py-1.5 rounded-xl text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
                          Approve Batch
                        </button>
                      )}
                      <button onClick={() => handleDelete(selectedBatch.id)}
                        className="p-1.5 rounded-xl text-rose-500 hover:bg-rose-50 transition-colors" title="Delete batch">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </div>

                  {/* Photo filter tabs */}
                  {selectedBatch.photos && selectedBatch.photos.length > 0 && (
                    <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 mb-4 flex-wrap">
                      {[
                        { id: 'all', label: 'All', count: selectedBatch.photos.length },
                        { id: 'encoded', label: 'Encoded', count: selectedBatch.photos.filter(p => p.is_encoded).length },
                        { id: 'not_encoded', label: 'Not Encoded', count: selectedBatch.photos.filter(p => p.status === 'approved' && !p.is_encoded).length },
                        { id: 'pending', label: 'Pending', count: selectedBatch.photos.filter(p => p.status === 'pending').length },
                        { id: 'rejected', label: 'Rejected', count: selectedBatch.photos.filter(p => p.status === 'rejected').length },
                      ].map((tab) => (
                        <button key={tab.id} onClick={() => setPhotoFilter(tab.id)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${photoFilter === tab.id ? 'bg-white shadow-sm text-violet-700' : 'text-slate-500 hover:text-slate-700'}`}>
                          {tab.label} ({tab.count})
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Encoding progress */}
                  {encodingInProgress && encodingProgress && (
                    <div className="mb-4 p-3 bg-violet-50 border border-violet-200 rounded-2xl">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-violet-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                        <span className="text-sm text-violet-700 font-medium flex-1">{encodingProgress.message || 'Encoding in progress…'}</span>
                        {encodingProgress.total > 0 && <span className="text-xs text-violet-500">{encodingProgress.current}/{encodingProgress.total}</span>}
                      </div>
                      {encodingProgress.total > 0 && (
                        <div className="mt-2 h-1.5 bg-violet-200 rounded-full overflow-hidden">
                          <div className="h-1.5 bg-violet-600 rounded-full transition-all duration-300" style={{ width: `${(encodingProgress.current / encodingProgress.total) * 100}%` }} />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Photos grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {(() => {
                      let fp = selectedBatch.photos || [];
                      if (photoFilter === 'encoded') fp = fp.filter(p => p.is_encoded);
                      else if (photoFilter === 'not_encoded') fp = fp.filter(p => p.status === 'approved' && !p.is_encoded);
                      else if (photoFilter === 'pending') fp = fp.filter(p => p.status === 'pending');
                      else if (photoFilter === 'rejected') fp = fp.filter(p => p.status === 'rejected');
                      if (fp.length === 0) return <div className="col-span-full py-8 text-center text-slate-400 text-sm">No photos for this filter</div>;
                      return fp.map((photo) => (
                        <div key={photo.id} className="bg-slate-50 rounded-xl overflow-hidden border border-slate-200">
                          <div className="relative aspect-square">
                            <img
                              src={getLocalThumbnailUrl(photo) || (photo.drive_file_id ? getDriveThumbnailUrl(photo.drive_file_id) : resolveUploadsPath(photo.filepath))}
                              alt={photo.filename}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                if (photo.drive_file_id) { e.target.style.display = 'none'; }
                                else { const alt = resolveBackendPath(photo.filepath); if (alt && e.target.src !== alt) e.target.src = alt; else e.target.style.display = 'none'; }
                              }}
                            />
                            <button onClick={(e) => { e.stopPropagation(); window.open(getLocalImageUrl(photo) || getDriveImageUrl(photo.drive_file_id) || resolveUploadsPath(photo.filepath), '_blank'); }}
                              className="absolute top-1 right-1 p-1 rounded-lg bg-black/40 text-white hover:bg-black/60 transition-colors" title="View original">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            </button>
                          </div>
                          <div className="px-2 py-1.5 space-y-1">
                            <div className="flex flex-wrap gap-1">
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${photoStatusBadgeCls(photo)}`}>{photo.status}</span>
                              {photo.status === 'approved' && (
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${photo.is_encoded ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                                  {photo.is_encoded ? 'Encoded' : 'Not Encoded'}
                                </span>
                              )}
                            </div>
                            {photo.status === 'pending' && (
                              <div className="flex gap-1">
                                <button onClick={(e) => { e.stopPropagation(); handlePhotoAction(photo.id, 'approve'); }}
                                  className="flex-1 py-0.5 rounded-lg text-[9px] font-bold bg-emerald-600 text-white hover:bg-emerald-700">Approve</button>
                                <button onClick={(e) => { e.stopPropagation(); handlePhotoAction(photo.id, 'reject'); }}
                                  className="flex-1 py-0.5 rounded-lg text-[9px] font-bold bg-rose-600 text-white hover:bg-rose-700">Reject</button>
                              </div>
                            )}
                            {photo.status === 'approved' && !photo.is_encoded && (
                              <button onClick={(e) => { e.stopPropagation(); handleRetryEncoding(photo.id); }}
                                className="w-full py-0.5 rounded-lg text-[9px] font-bold text-white"
                                style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                                Retry
                              </button>
                            )}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <svg className="w-7 h-7 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </div>
              <p className="text-slate-400 text-sm">Select a batch to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Batches;

